import React, { useMemo, useState } from 'react'

import { ImageLightbox } from '@/shared/components/ProductCarousel/ImageLightbox/ImageLightbox'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Divider, styled, Typography } from '@mui/material'

interface ReviewPhotosGalleryProps {
  photos: string[]
}

export const ReviewPhotosGallery: React.FC<ReviewPhotosGalleryProps> = ({ photos }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const lightboxImages = useMemo(
    () => photos.map((src) => ({ src, loading: 'lazy' as const, alt: 'Customer review photo' })),
    [photos],
  )

  if (photos.length === 0) return null

  const handleOpen = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  return (
    <StyledSection>
      <Divider sx={{ mb: 4 }} />

      <Typography variant="h5" component="h2" gutterBottom>
        Photos from Reviews
      </Typography>

      <StyledScrollContainer>
        {photos.map((photo, index) => (
          <StyledPhoto
            key={photo}
            src={photo}
            alt="Customer review photo"
            loading="lazy"
            onClick={() => handleOpen(index)}
          />
        ))}
      </StyledScrollContainer>

      <ImageLightbox
        open={lightboxOpen}
        currentImageIndex={lightboxIndex}
        setCurrentIndex={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
      />
    </StyledSection>
  )
}

const StyledSection = styled(Box)`
  padding: 0 0 ${pxToRem(48)};
`

const StyledScrollContainer = styled(Box)`
  display: flex;
  gap: ${pxToRem(12)};
  overflow-x: auto;
  white-space: nowrap;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${pxToRem(8)};

  &::-webkit-scrollbar {
    height: ${pxToRem(6)};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.palette.divider};
    border-radius: ${pxToRem(3)};
  }
`

const StyledPhoto = styled('img')`
  height: ${pxToRem(200)};
  width: auto;
  border-radius: ${pxToRem(8)};
  scroll-snap-align: start;
  flex-shrink: 0;
  object-fit: cover;
  cursor: zoom-in;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`
